import { type JSX, useMemo } from 'react';
import { cx } from '@linaria/core';

import { useTranslation } from '@cx-modules/i18n-provider';
import type { OddsFormat } from '@cx-modules/types';

import { useOddsGridContext } from '../../context/OddsGridContext';
import { formatOddsText } from '../../helpers/format-odds';
import { useBookmakersAll } from '../../hooks/useBookmakersAll';
import { useCalculateNumberOfGridCells } from '../../hooks/useCalculateNumberOfGridCells';
import { useNonFavouritedExchangeIds } from '../../hooks/useNonFavouritedExchangeIds';
import { useNonFavouritedSportsBookIds } from '../../hooks/useNonFavouritedSportsBookIds';
import type { Bet, Odd } from '../../types';
import { GridLayout } from '../GridLayout';
import { ExchangeOddsCell, SportsbookOddsCell } from '../OddsCell';
import { OddsHeadingMobile } from '../OddsHeadingMobile/OddsHeadingMobile';
import { BookmakerSeparator } from '../shared-styles';

import { oddsAreaWrapper } from './style';

interface OddsCellsDefaultProps {
  bet: Bet;
  betsWithBestOdds: (Bet & { bestOdds: number; bestOddsFractional: string; bestOddsText: string | number })[];
  oddsFormat: OddsFormat;
}

export const OddsCellsDefault = ({ bet, betsWithBestOdds, oddsFormat }: OddsCellsDefaultProps): JSX.Element => {
  const { isTabletOrDesktop } = useOddsGridContext();
  const nonFavouritedSportsbookIds = useNonFavouritedSportsBookIds();
  const nonFavouritedExchangeIds = useNonFavouritedExchangeIds();

  const myBookmakersDesktop: never[] = [];
  const myBookmakers = isTabletOrDesktop ? myBookmakersDesktop : [];

  const { t } = useTranslation('label');

  const sportsbooksLabel = t('CX_SPORTSBOOKS', { ns: 'label' });
  const exchangeLabel = t('CX_EXCHANGE', { ns: 'label' });

  const bookmakers = useBookmakersAll();

  const betWithBestOdds = betsWithBestOdds.find(b => b.id === bet.id);
  const bestOddsValue = betWithBestOdds?.bestOdds;
  const bestBookieCodes = useMemo(() => {
    if (!betWithBestOdds || !bestOddsValue) return new Set<string>();
    return new Set(
      betWithBestOdds.odds.filter((odd: Odd) => odd.decimal === bestOddsValue).map(odd => odd.bookmakerCode),
    );
  }, [betWithBestOdds, bestOddsValue]);

  const oddsDataMap = useMemo(() => {
    const map = new Map<string, { odds: Odd | undefined; oddsText: string | number | undefined }>();
    if (!bet) return map;
    bet.odds.forEach(odd => {
      map.set(odd.bookmakerCode, {
        odds: odd,
        oddsText: formatOddsText(odd, oddsFormat),
      });
    });
    return map;
  }, [bet, oddsFormat]);

  const hasActiveExchangeOdds = useMemo(
    () =>
      bookmakers.some(
        bookmaker =>
          bookmaker.type.toLowerCase() === 'exchange' &&
          bet.odds.some(odd => odd.bookmakerCode === bookmaker.code && odd.status.toLowerCase() === 'active'),
      ),
    [bookmakers, bet.odds],
  );
  const hasActiveSportsbookOdds = useMemo(
    () =>
      bookmakers.some(
        bookmaker =>
          bookmaker.type.toLowerCase() === 'traditional' &&
          bet.odds.some(odd => odd.bookmakerCode === bookmaker.code && odd.status.toLowerCase() === 'active'),
      ),
    [bookmakers, bet.odds],
  );

  const numberOfCells = useCalculateNumberOfGridCells();

  if (!bet) {
    return <GridLayout className={cx(oddsAreaWrapper)} id="odds-row" numberOfCells={numberOfCells} />;
  }

  return (
    <GridLayout className={cx(oddsAreaWrapper)} id="odds-row" numberOfCells={numberOfCells}>
      {myBookmakers.length > 0 && (
        <>
          {myBookmakers.map(bookie => {
            const oddsData = oddsDataMap.get(bookie);
            return (
              <SportsbookOddsCell
                bet={bet}
                bookieCode={bookie}
                isBest={bestBookieCodes.has(bookie)}
                key={`${bet.id}_${bookie}`}
                odds={oddsData?.odds}
                oddsFormat={oddsFormat}
                oddsText={oddsData?.oddsText}
              />
            );
          })}
          {hasActiveSportsbookOdds && <BookmakerSeparator />}
        </>
      )}

      {hasActiveSportsbookOdds && <OddsHeadingMobile>{sportsbooksLabel}</OddsHeadingMobile>}
      {nonFavouritedSportsbookIds.length > 0 &&
        nonFavouritedSportsbookIds.map(bookie => {
          const oddsData = oddsDataMap.get(bookie);
          return (
            <SportsbookOddsCell
              bet={bet}
              bookieCode={bookie}
              isBest={bestBookieCodes.has(bookie)}
              key={`${bet.id}_${bookie}`}
              odds={oddsData?.odds}
              oddsFormat={oddsFormat}
              oddsText={oddsData?.oddsText}
            />
          );
        })}
      {hasActiveExchangeOdds && (
        <>
          <OddsHeadingMobile>{exchangeLabel}</OddsHeadingMobile>
          <BookmakerSeparator />
        </>
      )}
      {nonFavouritedExchangeIds.length > 0 &&
        nonFavouritedExchangeIds.map(bookie => {
          const oddsData = oddsDataMap.get(bookie);
          return (
            <ExchangeOddsCell
              bet={bet}
              bookieCode={bookie}
              isBest={bestBookieCodes.has(bookie)}
              key={`${bet.id}_${bookie}`}
              odds={oddsData?.odds}
              oddsFormat={oddsFormat}
              oddsText={oddsData?.oddsText}
            />
          );
        })}
    </GridLayout>
  );
};
